import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { PAGE_ANCHOR_QUERY_PARAM } from '@/constants/pageAnchor';
import { usePageAnchors } from '@/contexts/pageAnchorsContext';

/**
 * Jumps to the card a url names, so a link can land inside a page rather than at its top.
 *
 * Two spellings are honoured: `?section=care-team`, which the rail writes whenever someone jumps
 * to a card so the address bar is always a shareable link to where they are, and the older
 * `#care-team` fragment that pages already use to link into one another. The query wins when both
 * are present, since it is the one the rail keeps current.
 *
 * Waits for the card to register itself, since a page's cards mount as their data arrives, and
 * jumps once per target so that dragging the cards around afterwards does not pull the reader
 * back.
 */
const usePageAnchorFromUrl = () => {
  const { hash, search } = useLocation();
  const { anchors, scrollToAnchor } = usePageAnchors();
  const jumpedTo = useRef<string>(undefined);

  const id = new URLSearchParams(search).get(PAGE_ANCHOR_QUERY_PARAM) || hash.slice(1);

  useEffect(() => {
    if (!id || jumpedTo.current === id) return;
    if (!anchors.some((anchor) => anchor.id === id)) return;

    jumpedTo.current = id;
    scrollToAnchor(id);
  }, [anchors, id, scrollToAnchor]);
};

export default usePageAnchorFromUrl;
